import { useState, useEffect, useCallback } from 'react'
import { showToast } from '@/components/layout/Toast'
import { IconBriefcase, IconCheck } from '@/components/icons/Icons'

/**
 * Mandar una tarea del backlog a su proyecto.
 *
 * El backlog ya era la bandeja de lo que Noa extrae y no supo dónde poner; lo que
 * faltaba era poder vaciarla sin abrir el Kanban y arrastrar.
 *
 * ─── Por qué sugiere en vez de listar todo ───
 *
 * Los candidatos salen de la misma cuenta que hace Noa al extraer: los proyectos de las
 * empresas donde figura el contacto, más los de los productos que ese cliente consume.
 * Ofrecer la lista entera del ERP obligaría a recordar de quién es cada proyecto; ofrecer
 * sólo los probables convierte la decisión en un clic.
 *
 * Igual se puede elegir cualquier otro: la sugerencia es ayuda, no una reja.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface Proyecto {
  id: number
  nombre: string
  descripcion?: string | null
}

interface Props {
  tareaId: number
  /** Todos los proyectos activos, para el caso en que ninguno de los sugeridos sirva. */
  todos: Proyecto[]
  onAsignada: (proyectoId: number) => void
}

export default function AsignarProyecto({ tareaId, todos, onAsignada }: Props) {
  const [sugeridos, setSugeridos] = useState<Proyecto[]>([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState<number | null>(null)
  const [verTodos, setVerTodos] = useState(false)

  const token = () => localStorage.getItem('auth_token')

  const cargar = useCallback(async () => {
    setCargando(true)
    setVerTodos(false)
    try {
      const res = await fetch(`${API_URL}/api/backlog/${tareaId}/candidatos`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (res.ok) {
        const d = await res.json()
        setSugeridos(d.candidatos ?? [])
      }
    } catch {
      /* sin sugerencias se puede elegir igual de la lista completa */
    } finally {
      setCargando(false)
    }
  }, [tareaId])

  useEffect(() => { void cargar() }, [cargar])

  const asignar = async (proyectoId: number) => {
    setGuardando(proyectoId)
    try {
      const res = await fetch(`${API_URL}/api/backlog/${tareaId}/proyecto`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ proyecto_id: proyectoId }),
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({}))
        showToast(e.error ?? 'No se pudo asignar', 'error')
        return
      }
      const nombre = [...sugeridos, ...todos].find(p => p.id === proyectoId)?.nombre ?? 'el proyecto'
      showToast(`Movida a ${nombre}`, 'success')
      onAsignada(proyectoId)
    } catch {
      showToast('No se pudo asignar', 'error')
    } finally {
      setGuardando(null)
    }
  }

  // Los que no están sugeridos, para la lista completa.
  const resto = todos.filter(t => !sugeridos.some(s => s.id === t.id))
  const aMostrar = verTodos ? [...sugeridos, ...resto] : sugeridos

  return (
    <div className="asignar-proyecto">
      <h4><IconBriefcase size={14} /> ¿A qué proyecto va?</h4>

      {cargando ? (
        <p className="asignar-proyecto__vacio">Buscando proyectos…</p>
      ) : (
        <>
          {sugeridos.length === 0 && !verTodos && (
            <p className="asignar-proyecto__vacio">
              Este contacto no tiene proyectos activos ni usa un producto nuestro, así que
              no hay nada que sugerir.
            </p>
          )}

          <div className="asignar-proyecto__opciones">
            {aMostrar.map(p => {
              const esSugerido = sugeridos.some(s => s.id === p.id)
              return (
                <button
                  key={p.id}
                  className={`asignar-proyecto__opcion${esSugerido ? ' asignar-proyecto__opcion--sugerido' : ''}`}
                  onClick={() => void asignar(p.id)}
                  disabled={guardando !== null}
                  type="button"
                >
                  <span className="asignar-proyecto__nombre">
                    {guardando === p.id ? <IconCheck size={13} /> : null} {p.nombre}
                  </span>
                  {p.descripcion && (
                    <span className="asignar-proyecto__que">{p.descripcion.slice(0, 90)}</span>
                  )}
                </button>
              )
            })}
          </div>

          {!verTodos && resto.length > 0 && (
            <button className="asignar-proyecto__mas" onClick={() => setVerTodos(true)} type="button">
              Ver los {resto.length} proyectos restantes
            </button>
          )}
        </>
      )}
    </div>
  )
}
